import axios from 'axios';
import { validTokenExists } from '../../firebaseHelper';
import { GET_STARTED_NEW_USER_RESPONSE } from '../../constants/local_storage_constants';
import { isTransientRequestFailure, withTransientRetry } from '../../utils/requestRetry';
import { readOnboardingHomePicks } from '../../utils/onboardingSession';
import { mergeForMePreviews } from '../../utils/forMePreviews';
import {
    FOR_ME_LOADING_COUNT_DECREASE,
    FOR_ME_LOADING_COUNT_INCREASE,
    FOR_ME_LOAD_FAILED,
    FOR_ME_PREFERENCE_SAVE_STARTED,
    FOR_ME_PREFERENCE_SAVE_FINISHED,
    FOR_ME_PREFERENCE_REFRESH_REQUESTED,
    FOR_ME_PREFERENCE_REFRESH_READY,
    FOR_ME_PREFERENCE_REFRESH_FAILED,
    FOR_ME_PREFERENCE_REFRESH_APPLIED,
    FOR_ME_PREVIEW_SEEN,
    SET_FOR_ME,
} from '../types';

// One request per store, including across account changes. A superseded request
// drains before the latest preferences generate their replacement.
const requests = new WeakMap();
let saveSequence = 0;
const hasPendingSaves = (user) => Object.keys(user.forMePreferenceSaves || {}).length > 0;

export const beginForMePreferenceSave = (dispatch, uid) => {
    const token = ++saveSequence;
    dispatch({ type: FOR_ME_PREFERENCE_SAVE_STARTED, payload: { uid, token } });
    return token;
};

export const finishForMePreferenceSave = (dispatch, getState, uid, token, succeeded, boostTopicId) => {
    dispatch({
        type: FOR_ME_PREFERENCE_SAVE_FINISHED,
        payload: { uid, token, succeeded, boostTopicId },
    });
    const user = getState().user;
    if (user.uid === uid && user.preferenceFeedRefreshState === 'refreshing') {
        return loadForMePreviews({ prepare: true })(dispatch, getState);
    }
};

const pendingGuestId = () => {
    try {
        return JSON.parse(localStorage.getItem(GET_STARTED_NEW_USER_RESPONSE))?.newId || null;
    } catch (_) {
        localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
        return null;
    }
};

export const prepareForMeWithLatestPreferences = () => (dispatch, getState) => {
    const user = getState().user;
    if (!user.authenticated || !user.uid || user.preferenceFeedRefreshState === 'ready') {
        return Promise.resolve(null);
    }
    dispatch({ type: FOR_ME_PREFERENCE_REFRESH_REQUESTED, payload: { uid: user.uid } });
    return loadForMePreviews({ prepare: true })(dispatch, getState);
};

export const applyPreparedForMe = () => (dispatch, getState) => {
    const user = getState().user;
    if (!user.authenticated || user.preferenceFeedRefreshState !== 'ready' || hasPendingSaves(user) ||
        user.preparedForMeRevision !== user.forMePreferenceRevision) return false;
    dispatch({ type: FOR_ME_PREFERENCE_REFRESH_APPLIED, payload: { uid: user.uid } });
    return true;
};

export const recordForMePreviewSeen = (uid, previewId) => ({
    type: FOR_ME_PREVIEW_SEEN, payload: { uid, previewId },
});

export const retryForMeRefreshIfNeeded = () => (dispatch, getState) => {
    const active = requests.get(dispatch);
    if (active) {
        active.retryAfterCompletion = true;
        return active.promise;
    }
    const user = getState().user;
    if (!user.forMeLoadError && user.preferenceFeedRefreshState !== 'failed') return Promise.resolve(null);
    return user.preferenceFeedRefreshState === 'failed'
        ? prepareForMeWithLatestPreferences()(dispatch, getState)
        : loadForMePreviews()(dispatch, getState);
};

export const loadForMePreviews = (options = {}) => (dispatch, getState) => {
    const user = getState().user;
    if (!user.authenticated || !user.uid) return Promise.resolve(null);
    // A failed preference rebuild must not fall back to the server's old cached queue.
    if (!options.prepare && user.preferenceFeedRefreshState === 'failed') {
        return prepareForMeWithLatestPreferences()(dispatch, getState);
    }
    const prepare = options.prepare === true;
    const forceRefresh = prepare || options.forceRefresh === true;
    if (prepare && user.preferenceFeedRefreshState === 'ready') return Promise.resolve(user.preparedForMeData);
    if (!prepare && user.preferenceFeedRefreshState === 'ready') {
        if (forceRefresh) applyPreparedForMe()(dispatch, getState);
        return Promise.resolve(null);
    }
    if (!prepare && user.preferenceFeedRefreshState === 'refreshing') return Promise.resolve(null);
    if (hasPendingSaves(user)) return Promise.resolve(null);
    if (requests.has(dispatch)) return requests.get(dispatch).promise;

    const uid = user.uid;
    const revision = user.forMePreferenceRevision;
    const sessionRevision = user.forMeSessionRevision;
    const current = () => {
        const latest = getState().user;
        return latest.authenticated && latest.uid === uid && latest.forMePreferenceRevision === revision &&
            latest.forMeSessionRevision === sessionRevision && !hasPendingSaves(latest) &&
            (prepare || (latest.preferenceFeedRefreshState || 'idle') === 'idle');
    };
    dispatch({ type: FOR_ME_LOADING_COUNT_INCREASE, payload: { uid, sessionRevision } });
    const active = { promise: null, retryAfterCompletion: false };
    let recoverAfterReconnect = false;
    const request = (async () => {
        try {
            const newId = pendingGuestId();
            const body = { newId };
            if (forceRefresh) body.forceRefresh = true;
            if (prepare && user.forMePreferenceBoostTopicIds?.length) {
                body.boostTopicIDs = user.forMePreferenceBoostTopicIds;
            }
            const response = await withTransientRetry(async () => {
                // This attempt consumes any reconnect received before it.
                active.retryAfterCompletion = false;
                await validTokenExists();
                if (!current()) return null;
                return axios.post('/for_me', body);
            }, { shouldContinue: current });
            if (!current()) return null;
            const { data } = response;
            if (!Array.isArray(data?.previews) || data.error || data.success === false) {
                throw new Error(data?.error || 'Unable to refresh your feed.');
            }
            if (newId && pendingGuestId() === newId) localStorage.removeItem(GET_STARTED_NEW_USER_RESPONSE);
            dispatch(prepare ? {
                type: FOR_ME_PREFERENCE_REFRESH_READY, payload: { uid, revision, data },
            } : { type: SET_FOR_ME, payload: data });
            if (prepare) {
                const latest = getState().user;
                const picks = readOnboardingHomePicks(uid);
                const visible = mergeForMePreviews(picks, latest.forMePreviewsData?.previews,
                    latest.forMeDismissedInitialPreviewIds);
                const candidate = mergeForMePreviews(picks, data.previews,
                    latest.forMeDismissedInitialPreviewIds, latest.forMeSeenPreviewIds);
                const unchanged = visible.length === candidate.length &&
                    visible.every((preview, index) => preview.id === candidate[index].id);
                if (!visible.length || unchanged) applyPreparedForMe()(dispatch, getState);
            }
            return data;
        } catch (error) {
            if (!current()) return null;
            recoverAfterReconnect = active.retryAfterCompletion && isTransientRequestFailure(error);
            if (prepare) {
                dispatch({ type: FOR_ME_PREFERENCE_REFRESH_FAILED, payload: { uid, revision } });
            } else {
                dispatch({ type: FOR_ME_LOAD_FAILED, payload: { uid, revision, sessionRevision } });
            }
            return null;
        } finally {
            requests.delete(dispatch);
            dispatch({ type: FOR_ME_LOADING_COUNT_DECREASE, payload: { uid, sessionRevision } });
            const latest = getState().user;
            if (recoverAfterReconnect && current()) {
                retryForMeRefreshIfNeeded()(dispatch, getState);
            } else if (latest.authenticated && latest.preferenceFeedRefreshState === 'refreshing' && !hasPendingSaves(latest)) {
                loadForMePreviews({ prepare: true })(dispatch, getState);
            } else if (latest.authenticated && !latest.forMePreviewsData && !hasPendingSaves(latest) &&
                (latest.uid !== uid || latest.forMeSessionRevision !== sessionRevision || latest.forMePreferenceRevision !== revision)) {
                loadForMePreviews()(dispatch, getState);
            }
        }
    })();
    active.promise = request;
    requests.set(dispatch, active);
    return request;
};
